import {
  Box,
  Button,
  Center,
  Flex,
  Spacer,
  Stack,
  StackDivider,
  useToast,
} from '@chakra-ui/react';
import { get } from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';

import { type Control, type UseFormWatch, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from 'urql';

import {
  isRoleRevelioAdmin,
  RouterRoutePaths,
  useGetLoggedInUser,
} from '@revelio/auth';
import { Loading } from '@revelio/core';
import { USER_DETAILS_QUERY } from '@revelio/data-access';

import { FieldGroup } from '../../../shared/ui/admin/groups/field/field-group';
import DashboardPage from '../../DashboardPage';
import { CacheBypassOptions } from '../admin-user-edit/cache-bypass-options';
import ClientForm, { type ClientFormValues } from '../client-form';
import { useDeleteUser } from '../hooks/use-delete-user';
import UserForm, { submitEditUser, type UserFormValues } from '../user-form';
import { ClientDetailsQuery, UpdateUserMutation } from '../userOperations';

const UserEdit = () => {
  const toast = useToast();

  const { userId } = useParams();
  const {
    loggedInUser,
    query: [{ fetching: loggedInUserLoading }],
  } = useGetLoggedInUser();

  const isRevelioAdmin = isRoleRevelioAdmin(loggedInUser?.role);
  const [{ fetching, data }] = useQuery({
    query: USER_DETAILS_QUERY,
    variables: { ids: [userId as string] },
    pause: !loggedInUser,
  });
  const userToEdit = get(data, 'users[0]', {});
  const isEditingOwnUser = loggedInUser?.id === userId;
  const isSelfAndRevelioAdmin = isEditingOwnUser && isRevelioAdmin;
  const [{ fetching: fetchingClient, data: clientData }] = useQuery({
    query: ClientDetailsQuery,
    variables: { ids: [userToEdit.client_name] },
    pause: !userToEdit.client_name,
    requestPolicy: 'cache-and-network',
  });
  const userClientSettings = get(clientData, 'clients[0]', {});
  const [, updateUser] = useMutation(UpdateUserMutation);

  const userFormMethods = useForm<UserFormValues>();
  const clientReadonlyForm = useForm<ClientFormValues>();
  const navigate = useNavigate();
  const deleteUser = useDeleteUser();
  return (
    <DashboardPage title="Manage" hideTop loading={false}>
      <Box maxW="5xl" mx="auto">
        <Stack as="section" spacing="6">
          <Box bg="white" shadow="sm" rounded="lg" p="4">
            {!fetching &&
            userToEdit &&
            !fetchingClient &&
            !loggedInUserLoading ? (
              <Stack direction="row" divider={<StackDivider />} spacing="6">
                <form
                  style={{ width: '930px' }}
                  onSubmit={userFormMethods.handleSubmit(
                    submitEditUser({
                      updateUser,
                      userId: userId as string,
                      toast,
                      currentUser: userToEdit,
                      loggedInUser,
                    })
                  )}
                >
                  <Stack divider={<StackDivider />} spacing="6">
                    <FieldGroup
                      title={isEditingOwnUser ? 'Your account' : `Editing user`}
                      description={`Update ${isEditingOwnUser ? 'your' : 'their'} details`}
                    >
                      <UserForm
                        useFormMethods={userFormMethods}
                        userToEdit={userToEdit}
                        setPassword={(newPassword: string) =>
                          userFormMethods.setValue('password', newPassword)
                        }
                        passwordRules={{
                          minLength: {
                            value: 4,
                            message: 'Minimum length should be 4',
                          },
                        }}
                      />

                      {isSelfAndRevelioAdmin && (
                        <Box mt={2}>
                          <CacheBypassOptions />
                        </Box>
                      )}
                    </FieldGroup>

                    <FieldGroup>
                      <Stack direction="row" spacing={2} align="center">
                        {!isRevelioAdmin && (
                          <Button
                            colorScheme="navyBlue"
                            variant="solid"
                            size="md"
                            onClick={() => navigate(RouterRoutePaths.MANAGE)}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          colorScheme="green"
                          variant="solid"
                          size="md"
                          isLoading={userFormMethods.formState.isSubmitting}
                          type="submit"
                        >
                          Update User
                        </Button>
                        <Button
                          colorScheme="red"
                          variant="solid"
                          size="md"
                          isDisabled={isEditingOwnUser}
                          onClick={() => {
                            if (!userId) return;
                            deleteUser(userId, () => {
                              navigate(RouterRoutePaths.MANAGE);
                            });
                          }}
                        >
                          Delete User
                        </Button>
                        <Spacer />
                      </Stack>
                    </FieldGroup>
                  </Stack>
                </form>

                <ClientForm
                  clientToEdit={userClientSettings}
                  readonly={true}
                  title="Your client group settings"
                  description="Get in touch with Revelio to make any changes"
                  register={() => ({})}
                  control={
                    clientReadonlyForm.control as unknown as Control<
                      ClientFormValues,
                      object
                    >
                  }
                  watch={
                    clientReadonlyForm.watch as unknown as UseFormWatch<ClientFormValues>
                  }
                />
              </Stack>
            ) : (
              <Flex justify="center" minHeight="200px">
                <Center>
                  <Loading />
                </Center>
              </Flex>
            )}
          </Box>
        </Stack>
      </Box>
    </DashboardPage>
  );
};

export default UserEdit;
